import { useState, useMemo, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/feature/Header';
import Footer from '../components/feature/Footer';
import { useProducts } from '../hooks/useProducts';
import { apiFetch } from '../lib/apiClient';
import type { OrderResource } from '../types/api';
import { platePatterns, plateExamples } from '../utils/platePatterns';
import { countries } from '../mocks/countries';

const LOCALE_MAP: Record<string, string> = {
  en: 'en', de: 'de', at: 'de', hu: 'hu',
  cz: 'cs', sk: 'sk', sl: 'sl', ro: 'ro', bg: 'bg',
};

function mapLocale(lang: string): string {
  return LOCALE_MAP[lang] ?? 'en';
}

const steps = [
  { id: 1, key: 'vehicle' },
  { id: 2, key: 'period' },
  { id: 3, key: 'email' },
  { id: 4, key: 'review' },
];

const VT_UI: Record<string, { key: string; icon: string }> = {
  'car': { key: 'car', icon: 'ri-car-line' },
  'motorcycle': { key: 'motorcycle', icon: 'ri-motorbike-line' },
  'trailer': { key: 'trailer', icon: 'ri-truck-line' },
  'van / minibus (d2)': { key: 'van', icon: 'ri-bus-line' },
  'cng / biomethane': { key: 'cng', icon: 'ri-gas-station-line' },
  'plug-in hybrid (eco)': { key: 'phev', icon: 'ri-car-line' },
};

const regCountries = [
  { code: 'DE', name: 'Germany' },
  { code: 'PL', name: 'Poland' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'AT', name: 'Austria' },
  { code: 'HU', name: 'Hungary' },
  { code: 'FR', name: 'France' },
  { code: 'IT', name: 'Italy' },
  { code: 'SK', name: 'Slovakia' },
  { code: 'RO', name: 'Romania' },
  { code: 'BE', name: 'Belgium' },
  { code: 'BG', name: 'Bulgaria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'DK', name: 'Denmark' },
  { code: 'ES', name: 'Spain' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'SE', name: 'Sweden' },
  { code: 'SI', name: 'Slovenia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'US', name: 'USA' },
];

export default function CheckoutPage() {
  const [params] = useSearchParams();
  const { t, i18n } = useTranslation();
  const { data: products, loading: productsLoading } = useProducts();

  const countryCode = params.get('country') || '';
  const periodParam = params.get('period') || '';
  const priceParam = parseFloat(params.get('price') || '0');

  const countryData = products?.find(c => c.code === countryCode.toUpperCase()) ?? null;
  const requiresVin = countryData?.fields.some(f => f.name === 'vin') ?? false;

  const [step, setStep] = useState(1);
  const [vehicleType, setVehicleType] = useState('car');
  const [regCountry, setRegCountry] = useState('PL');
  const [plate, setPlate] = useState('');
  const [vin, setVin] = useState('');
  const [period, setPeriod] = useState(periodParam);
  const [startDate, setStartDate] = useState('');
  const [email, setEmail] = useState('');

  const [plateError, setPlateError] = useState('');
  const [vinError, setVinError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [dateError, setDateError] = useState('');
  const [periodError, setPeriodError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const regCountryName = regCountries.find((c) => c.code === regCountry)?.name || regCountry;
  const today = new Date().toISOString().split('T')[0];

  useEffect(() => {
    setPeriod('');
  }, [vehicleType]);

  useEffect(() => {
    if (plate) validatePlate(plate);
  }, [regCountry]);

  const currentOptions = useMemo(() => {
    return countryData?.vehicle_types.find(vt => vt.name === vehicleType)?.prices ?? [];
  }, [countryData, vehicleType]);

  const availablePeriods = currentOptions.map(o => o.period);

  const selectedPrice = useMemo(() => {
    if (!period) return priceParam;
    const opt = currentOptions.find(o => o.period === period);
    return opt ? parseFloat(opt.price) : priceParam;
  }, [currentOptions, period, priceParam]);

  const productId = currentOptions.find(o => o.period === period)?.id ?? null;

  const currency = countryData?.currency ?? 'EUR';

  const validatePlate = (value: string) => {
    const cleaned = value.trim().replace(/[\s-]/g, '').toUpperCase();
    if (!cleaned) {
      setPlateError('License plate is required');
      return false;
    }
    if (cleaned.length < 2) {
      setPlateError('Enter a valid license plate');
      return false;
    }
    const pattern = platePatterns[regCountry];
    if (pattern && !pattern.test(cleaned)) {
      const hint = plateExamples[regCountry] ? plateExamples[regCountry] : '';
      setPlateError(`Plate format for ${regCountryName}:${hint}`);
      return false;
    }
    setPlateError('');
    return true;
  };

  const validateEmail = (value: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!value.trim()) {
      setEmailError('Email is required');
      return false;
    }
    if (!re.test(value)) {
      setEmailError('Enter a valid email address');
      return false;
    }
    setEmailError('');
    return true;
  };

  const validateVin = (value: string) => {
    const cleaned = value.trim().replace(/\s+/g, '');
    if (!cleaned) {
      setVinError('VIN is required');
      return false;
    }
    if (cleaned.length !== 17) {
      setVinError('VIN must be exactly 17 characters');
      return false;
    }
    setVinError('');
    return true;
  };

  const validateDate = (value: string) => {
    if (!value) {
      setDateError('Start date is required');
      return false;
    }
    const selected = new Date(value);
    const todayDate = new Date(today);
    if (isNaN(selected.getTime())) {
      setDateError('Enter a valid date');
      return false;
    }
    const currentYear = todayDate.getFullYear();
    if (selected.getFullYear() < currentYear || selected.getFullYear() > currentYear + 2) {
      setDateError('Enter a valid date');
      return false;
    }
    if (selected < todayDate) {
      setDateError('Start date cannot be in the past');
      return false;
    }
    setDateError('');
    return true;
  };

  const canProceed = () => {
    if (step === 1) {
      const plateOk = validatePlate(plate);
      const vinOk = requiresVin ? validateVin(vin) : true;
      return plateOk && vinOk;
    }
    if (step === 2) {
      const periodOk = !!period;
      if (!periodOk) setPeriodError('Please select a validity period');
      else setPeriodError('');
      const dateOk = validateDate(startDate);
      return periodOk && dateOk;
    }
    if (step === 3) return validateEmail(email);
    return true;
  };

  const nextStep = () => {
    if (!canProceed()) return;
    if (step < 4) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        product_id: productId,
        country_registration: regCountry,
        license_plate: plate.trim().replace(/[\s-]/g, '').toUpperCase(),
        email,
        locale: mapLocale(i18n.language),
        start_date: startDate,
        ...(requiresVin ? { vin: vin.trim().toUpperCase() } : {}),
      };
      const result = await apiFetch<{ data: OrderResource }>('/v1/orders', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (result.data.payment_url) {
        window.location.href = result.data.payment_url;
      }
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  const vehicleTypes = countryData?.vehicle_types ?? [];
  const countryName = countryData?.name ?? countryCode.toUpperCase();
  const CC2KEY: Record<string, string> = {
    at: 'austria', hu: 'hungary', ch: 'switzerland', cz: 'czechRepublic',
    sk: 'slovakia', si: 'slovenia', ro: 'romania', bg: 'bulgaria',
  };
  const localizedCountry = CC2KEY[countryCode] ? t(`countries.${CC2KEY[countryCode]}.name`) : countryName;
  const periodLabel = (p: string) => t(`countryPage.period.${p.toLowerCase().replace(/\s+/g, '')}`, { defaultValue: p });
  const vehicleLabel = VT_UI[vehicleType] ? t(`checkout.vt.${VT_UI[vehicleType].key}.label`) : vehicleType;
  const countryPath = countries.find(c => c.code.toLowerCase() === countryCode)?.path ?? '/';

  return (
    <div className="min-h-screen flex flex-col bg-bg">
      <Header />
      <main className="flex-1">
        <div className="border-b border-border bg-surface">
          <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
            <Link
              to={countryPath}
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-muted transition hover:text-ink"
            >
              <i className="ri-arrow-left-line text-[14px]" />
              {t('checkout.backToLanding')}
            </Link>
            <span className="text-[12px] font-semibold uppercase tracking-wider text-muted">
              {t('checkout.stepOf', { current: step, total: 4 })}
            </span>
          </div>

          {/* Progress bar */}
          <div className="mx-auto max-w-3xl px-5 pb-4">
            <div className="flex items-center gap-2">
              {steps.map((s, idx) => (
                <div key={s.id} className="flex flex-1 items-center gap-2">
                  <div
                    className={`h-1 flex-1 rounded-full transition ${
                      s.id <= step ? 'bg-primary' : 'bg-border'
                    }`}
                  />
                  {idx < steps.length - 1 && (
                    <div className="hidden sm:block w-2" />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-2 hidden sm:grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
              {steps.map((s) => (
                <div
                  key={s.id}
                  className={`text-[12px] font-medium transition ${
                    s.id === step
                      ? 'text-ink font-semibold'
                      : s.id < step
                        ? 'text-primary'
                        : 'text-muted'
                  }`}
                >
                  {s.id}. {t(`checkout.step.${s.key}`)}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="mx-auto max-w-3xl px-5 py-10">
          {/* STEP 1 — Vehicle */}
          {step === 1 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.yourVehicle')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.yourVehicleSubtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                {/* Vehicle type */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.vehicleType')}
                  </label>
                  {productsLoading ? (
                    <div className="text-[14px] text-muted">Loading vehicle types…</div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(vehicleTypes.length > 0 ? vehicleTypes : [{ id: 0, name: 'car', prices: [] }]).map((vt) => {
                        const ui = VT_UI[vt.name] ?? { key: '', icon: 'ri-car-line' };
                        const vtLabel = ui.key ? t(`checkout.vt.${ui.key}.label`) : vt.name;
                        const vtDesc = ui.key ? t(`checkout.vt.${ui.key}.desc`) : '';
                        return (
                          <button
                            key={vt.id}
                            type="button"
                            onClick={() => setVehicleType(vt.name)}
                            className={`flex items-center gap-4 rounded-lg border px-4 py-3.5 text-left transition cursor-pointer ${
                              vehicleType === vt.name
                                ? 'border-primary bg-primary-soft'
                                : 'border-border bg-white hover:border-primary'
                            }`}
                          >
                            <div
                              className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                                vehicleType === vt.name ? 'bg-primary text-white' : 'bg-bg text-muted'
                              }`}
                            >
                              <i className={`${ui.icon} text-[18px]`} />
                            </div>
                            <div>
                              <div
                                className={`text-[15px] font-semibold ${
                                  vehicleType === vt.name ? 'text-ink' : 'text-ink-soft'
                                }`}
                              >
                                {vtLabel}
                              </div>
                              <div className="text-[13px] text-muted">{vtDesc}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <p className="mt-3 text-[13px] text-muted">
                    {t('checkout.heavyTrucksNote')}
                  </p>
                </div>

                {/* Country of registration */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.countryOfRegistration')}
                  </label>
                  <div className="relative">
                    <select
                      value={regCountry}
                      onChange={(e) => setRegCountry(e.target.value)}
                      className="w-full appearance-none rounded-lg border border-border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition focus:border-primary cursor-pointer pr-10"
                    >
                      {regCountries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.name} ({c.code})
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none text-[18px]" />
                  </div>
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.countryOfRegistrationHint')}
                  </p>
                </div>

                {/* License plate */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.licensePlate')}
                  </label>
                  <div className="flex overflow-hidden rounded-lg border border-border focus-within:border-primary transition bg-white">
                    <div className="flex shrink-0 items-center bg-primary px-4">
                      <span className="text-[14px] font-bold text-white">{regCountry}</span>
                    </div>
                    <input
                      type="text"
                      value={plate}
                      onChange={(e) => {
                        setPlate(e.target.value.toUpperCase().replace(/[\s-]/g, ''));
                        if (plateError) validatePlate(e.target.value);
                      }}
                      onBlur={() => validatePlate(plate)}
                      placeholder="KJUYGUY7"
                      className="w-full bg-white px-4 py-3.5 text-[15px] font-semibold uppercase text-ink outline-none placeholder:font-normal placeholder:text-muted/60"
                    />
                  </div>
                  {plateError && (
                    <p className="mt-2 text-[13px] text-red-500">{plateError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.licensePlateHint')}
                  </p>
                </div>

                {/* VIN — countries that require it */}
                {requiresVin && (
                  <div>
                    <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                      VIN (Vehicle Identification Number)
                    </label>
                    <input
                      type="text"
                      value={vin}
                      onChange={(e) => {
                        setVin(e.target.value.toUpperCase());
                        if (vinError) validateVin(e.target.value);
                      }}
                      onBlur={() => validateVin(vin)}
                      placeholder="WWZZZ1KZBW000000"
                      className={`w-full rounded-lg border bg-white px-4 py-3.5 text-[15px] font-semibold uppercase text-ink outline-none placeholder:font-normal placeholder:text-muted/60 transition ${
                        vinError ? 'border-red-400' : 'border-border focus:border-primary'
                      }`}
                    />
                    {vinError && (
                      <p className="mt-2 text-[13px] text-red-500">{vinError}</p>
                    )}
                    <p className="mt-2 text-[13px] text-muted">
                      17 characters from your vehicle registration document.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap"
                >
                  {t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — Period */}
          {step === 2 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step2.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step2.subtitle', { country: localizedCountry })}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                {/* Period selection */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.validityPeriod')}
                  </label>
                  {productsLoading ? (
                    <div className="text-[14px] text-muted">Loading periods…</div>
                  ) : availablePeriods.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {currentOptions.map((opt) => {
                        const active = period === opt.period;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => { setPeriod(opt.period); setPeriodError(''); }}
                            className={`flex items-center justify-between rounded-lg border px-4 py-3.5 text-left transition cursor-pointer ${
                              active
                                ? 'border-primary bg-primary-soft'
                                : periodError
                                  ? 'border-red-400 bg-white hover:border-primary'
                                  : 'border-border bg-white hover:border-primary'
                            }`}
                          >
                            <span
                              className={`text-[15px] font-semibold ${
                                active ? 'text-ink' : 'text-ink-soft'
                              }`}
                            >
                              {periodLabel(opt.period)}
                            </span>
                            <span className="text-[14px] font-semibold text-primary">
                              {parseFloat(opt.price).toFixed(2)} {currency}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[14px] text-muted">No periods available.</p>
                  )}
                  {periodError && (
                    <p className="mt-2 text-[13px] text-red-500">{periodError}</p>
                  )}
                </div>

                {/* Start date */}
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.startDate')}
                  </label>
                  <input
                    type="date"
                    min={today}
                    value={startDate}
                    onChange={(e) => {
                      setStartDate(e.target.value);
                      if (dateError) validateDate(e.target.value);
                    }}
                    onBlur={() => startDate && validateDate(startDate)}
                    className={`w-full rounded-lg border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition focus:border-primary ${dateError ? 'border-red-400' : 'border-border'}`}
                  />
                  {dateError && (
                    <p className="mt-2 text-[13px] text-red-500">{dateError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.startDateHint')}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={productsLoading}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — Email */}
          {step === 3 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step3.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step3.subtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-6">
                <div>
                  <label className="block text-[12px] font-semibold uppercase tracking-wider text-muted mb-3">
                    {t('checkout.emailAddress')}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (emailError) validateEmail(e.target.value);
                    }}
                    onBlur={() => validateEmail(email)}
                    placeholder={t('checkout.emailPlaceholder')}
                    className={`w-full rounded-lg border bg-white px-4 py-3.5 text-[15px] text-ink outline-none transition placeholder:text-muted/60 focus:border-primary ${
                      emailError ? 'border-red-400' : 'border-border'
                    }`}
                  />
                  {emailError && (
                    <p className="mt-2 text-[13px] text-red-500">{emailError}</p>
                  )}
                  <p className="mt-2 text-[13px] text-muted">
                    {t('checkout.emailHint')}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
                <button
                  type="button"
                  onClick={nextStep}
                  className="inline-flex items-center justify-center rounded-lg bg-primary px-7 py-3 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer whitespace-nowrap"
                >
                  {t('checkout.continue')}
                </button>
              </div>
            </div>
          )}

          {/* STEP 4 — Review */}
          {step === 4 && (
            <div>
              <h1 className="text-[24px] font-bold tracking-tight text-ink">{t('checkout.step4.title')}</h1>
              <p className="mt-1 text-[15px] text-muted">
                {t('checkout.step4.subtitle')}
              </p>

              <div className="mt-6 rounded-xl border border-border bg-surface p-6 md:p-7 space-y-5">
                {/* Summary rows */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.review.vehicle')}</div>
                    <div className="text-[15px] font-semibold text-ink">{vehicleLabel}</div>
                    <div className="text-[13px] text-ink-soft">{regCountry} — {plate.trim().replace(/\s+/g, ' ').toUpperCase()}</div>
                    {requiresVin && vin && (
                      <div className="text-[13px] text-ink-soft">VIN: {vin.toUpperCase()}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.review.evignette')}</div>
                    <div className="text-[15px] font-semibold text-ink">{localizedCountry}</div>
                    <div className="text-[13px] text-ink-soft">
                      {t('checkout.review.periodFrom', { period: periodLabel(period), date: startDate })}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(2)}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-[13px] text-muted">{t('checkout.step3.title')}</div>
                    <div className="text-[15px] font-semibold text-ink">{email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setStep(3)}
                    className="text-[13px] font-medium text-primary hover:underline cursor-pointer"
                  >
                    {t('checkout.edit')}
                  </button>
                </div>

                <div className="h-px bg-border" />

                {/* Pricing */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="text-muted">{t('checkout.review.vignette')}</span>
                    <span className="font-semibold text-ink">
                      {selectedPrice.toFixed(2)} {currency}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[14px]">
                    <span className="text-muted">{t('checkout.review.serviceFee')}</span>
                    <span className="font-semibold text-ink">1.50 {currency}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-3">
                    <span className="text-[15px] font-semibold text-ink">{t('checkout.review.total')}</span>
                    <span className="text-[20px] font-bold text-primary">
                      {(selectedPrice + 1.5).toFixed(2)} {currency}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="mt-2 w-full rounded-lg bg-primary px-6 py-3.5 text-[15px] font-semibold text-white transition hover:bg-primary-hover cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Processing…' : t('checkout.proceedToPayment')}
                </button>

                <p className="text-center text-[12px] text-muted">
                  {t('checkout.paymentHint')}
                </p>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevStep}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-5 py-3 text-[14px] font-medium text-ink transition hover:border-primary cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line text-[14px]" />
                  {t('checkout.back')}
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

